/**
 * SO-05 deep links into the web build. The API's share pages redirect here:
 *   /f/:code?ref=&c=        friend invite (accepted after guest auth)
 *   /auth/email?token=      email magic link (SY-01.2)
 *   /t/:token               web → app transfer token (claimed on a fresh device)
 *   /parent/consent?token=  COPPA: a parent allows purchases (any device, no sign-in)
 *   /parent/revoke?token=   … and withdraws that consent
 *   /s/:id?ref=&c=          a share link (restock, daily, beat): the native apps claim this
 *                           path on the link host, so the game routes it itself
 *   /n/:type                a tapped notification (the push's `url`; the service worker and the
 *                           native apps open it): features/notifications opens what it is about
 * Any `?ref=&c=` is attribution for guest auth. Paths are relative to the app's base so the
 * build keeps working under a sub-path.
 *
 * A share link is resolved with GET /v1/l/:id (features/links) into the link of its type,
 * which the feature owning that mode plays in the app: `restock` (help mode), `daily`, `beat`.
 */
import type { LinkViewDto } from './dto.js';

export type DeepLink =
  | { kind: 'friend'; code: string }
  | { kind: 'email'; token: string }
  | { kind: 'transfer'; token: string }
  | { kind: 'parentConsent'; token: string }
  | { kind: 'parentRevoke'; token: string }
  /** /s/:id, not resolved yet */
  | { kind: 'shareLink'; id: string }
  /** /n/:type, a tapped notification of that server push type */
  | { kind: 'notification'; type: string }
  /** resolved share links: the link view says what to play */
  | { kind: 'restock'; linkId: string; view: LinkViewDto }
  | { kind: 'daily'; linkId: string; date: string; view: LinkViewDto }
  | { kind: 'beat'; linkId: string; view: LinkViewDto };

export interface ParsedLink {
  link: DeepLink | null;
  attribution: { ref?: string; campaign?: string } | null;
  /** the app root to show once the link is consumed (same origin, query cleaned) */
  cleanPath: string;
}

const CODE = /^[0-9A-Za-z_-]{4,64}$/;
/** share link ids (the API's LINK_ID: base62) */
export const SHARE_ID = /^[0-9A-Za-z]{6,16}$/;
/** server push types (PushType: offline_ready, friend_helped…) */
export const PUSH_TYPE = /^[a-z][a-z_]{1,31}$/;

export function parseDeepLink(href: string): ParsedLink {
  const u = new URL(href);
  const q = u.searchParams;
  const ref = q.get('ref');
  const campaign = q.get('c');
  const attribution = ref || campaign ? { ...(ref ? { ref: ref.slice(0, 64) } : {}), ...(campaign ? { campaign: campaign.slice(0, 64) } : {}) } : null;
  let link: DeepLink | null = null;
  let root = u.pathname;
  const f = /^(.*?)\/f\/([^/]+)\/?$/.exec(u.pathname);
  const t = /^(.*?)\/t\/([^/]+)\/?$/.exec(u.pathname);
  const e = /^(.*?)\/auth\/email\/?$/.exec(u.pathname);
  const pa = /^(.*?)\/parent\/(consent|revoke)\/?$/.exec(u.pathname);
  const sl = /^(.*?)\/s\/([^/]+)\/?$/.exec(u.pathname);
  const n = /^(.*?)\/n\/([^/]+)\/?$/.exec(u.pathname);
  if (f && CODE.test(f[2]!)) {
    link = { kind: 'friend', code: decodeURIComponent(f[2]!) };
    root = f[1]!;
  } else if (t && CODE.test(t[2]!)) {
    link = { kind: 'transfer', token: decodeURIComponent(t[2]!) };
    root = t[1]!;
  } else if (e) {
    const token = q.get('token');
    if (token && token.length >= 16) link = { kind: 'email', token };
    root = e[1]!;
  } else if (pa) {
    const token = q.get('token');
    if (token && token.length >= 16) link = { kind: pa[2] === 'consent' ? 'parentConsent' : 'parentRevoke', token };
    root = pa[1]!;
  } else if (sl) {
    if (SHARE_ID.test(sl[2]!)) link = { kind: 'shareLink', id: sl[2]! };
    root = sl[1]!;
  } else if (n) {
    if (PUSH_TYPE.test(n[2]!)) link = { kind: 'notification', type: n[2]! };
    root = n[1]!;
  }
  const keep = new URLSearchParams();
  for (const [k, v] of q) if (!['ref', 'c', 'token'].includes(k)) keep.set(k, v);
  const qs = keep.toString();
  return { link, attribution, cleanPath: `${root.replace(/\/$/, '')}/${qs ? `?${qs}` : ''}` };
}
